import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FormEvent } from "react";
import { ApiError, api } from "@/lib/apiClient";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { formatDateTime } from "@/lib/format";

interface KevoxBakJob {
  id: string;
  filename?: string | null;
  status?: string | null;
  progress?: number | null;
  error_message?: string | null;
  rows_read?: number | null;
  rows_written?: number | null;
  rows_failed?: number | null;
  errors_count?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface KevoxBakError {
  id?: number;
  source_table?: string | null;
  source_key?: string | null;
  reason?: string | null;
  created_at?: string | null;
}

const POLL_INTERVAL_MS = 5000;

export function KevoxBakSection() {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<KevoxBakJob[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [errors, setErrors] = useState<KevoxBakError[]>([]);

  const refreshJobs = useCallback(async () => {
    try {
      const data = await api.get<{ items: KevoxBakJob[] }>("/kevox/import-bak/jobs?limit=20");
      setJobs(Array.isArray(data?.items) ? data.items : []);
    } catch {
      // ignore polling errors silently
    }
  }, []);

  useEffect(() => {
    refreshJobs();
    const id = window.setInterval(() => {
      // Versteckter Tab: kein Hintergrund-Polling (Serverlast).
      if (document.visibilityState === "hidden") return;
      void refreshJobs();
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [refreshJobs]);

  useEffect(() => {
    if (!selectedJobId && jobs[0]) setSelectedJobId(jobs[0].id);
  }, [jobs, selectedJobId]);

  useEffect(() => {
    if (!selectedJobId) {
      setErrors([]);
      return;
    }
    let cancelled = false;
    api
      .get<{ items?: KevoxBakError[]; errors?: KevoxBakError[] } | KevoxBakError[]>(
        `/kevox/import-bak/${encodeURIComponent(selectedJobId)}/errors`,
      )
      .then((payload) => {
        if (cancelled) return;
        if (Array.isArray(payload)) setErrors(payload);
        else if (payload && Array.isArray(payload.items)) setErrors(payload.items);
        else if (payload && Array.isArray(payload.errors)) setErrors(payload.errors);
        else setErrors([]);
      })
      .catch(() => {
        if (!cancelled) setErrors([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedJobId, jobs]);

  const selectedJob = useMemo(
    () => jobs.find((j) => j.id === selectedJobId) || null,
    [jobs, selectedJobId],
  );

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const files = fileRef.current?.files;
    if (!files || files.length === 0) return;

    const fd = new FormData();
    for (const f of Array.from(files)) fd.append("files", f);

    setUploading(true);
    setUploadError(null);
    try {
      const resp = await api.postForm<{ items?: Array<{ job_id: string }> }>("/kevox/import-bak", fd);
      const firstId = resp?.items?.[0]?.job_id;
      if (firstId) setSelectedJobId(firstId);
      if (fileRef.current) fileRef.current.value = "";
      await refreshJobs();
    } catch (err) {
      setUploadError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Upload fehlgeschlagen.",
      );
    } finally {
      setUploading(false);
    }
  }

  return (
    <Card
      title="Kevox .bak importieren"
      description="Mehrere .bak-Dateien hochladen; der Import wird im Backend per Queue sequenziell verarbeitet."
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-3 md:flex-row md:items-end">
        <div className="flex-1">
          <label className="eh-label" htmlFor="kevox-bak-files">
            Dateien (.bak)
          </label>
          <input
            ref={fileRef}
            id="kevox-bak-files"
            type="file"
            accept=".bak"
            multiple
            required
            className="block w-full text-sm text-slate-700 file:mr-3 file:rounded file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium hover:file:bg-slate-200 dark:text-slate-200 dark:file:bg-slate-800 dark:file:text-slate-100 dark:hover:file:bg-slate-700"
          />
        </div>
        <Button type="submit" loading={uploading}>
          Import starten
        </Button>
      </form>
      {uploadError && (
        <div className="mt-3">
          <Alert variant="error" title="Fehler">
            {uploadError}
          </Alert>
        </div>
      )}

      {selectedJob && (
        <div className="mt-4 space-y-3">
          <ProgressBar value={selectedJob.progress ?? 0} />
          <div className="flex flex-wrap gap-4 text-sm text-slate-600 dark:text-slate-300">
            <Counter label="rows_read" value={selectedJob.rows_read ?? 0} />
            <Counter label="rows_written" value={selectedJob.rows_written ?? 0} />
            <Counter label="rows_failed" value={selectedJob.rows_failed ?? 0} />
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Status: <strong>{selectedJob.status || "—"}</strong>
            {selectedJob.error_message && <> · {selectedJob.error_message}</>}
          </p>
        </div>
      )}

      {jobs.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">Import-Läufe</h3>
          <ul className="mt-2 space-y-1">
            {jobs.map((job) => (
              <li key={job.id}>
                <button
                  type="button"
                  className={`flex w-full flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 text-left text-xs ${
                    selectedJobId === job.id
                      ? "border-brand-300 bg-brand-50 dark:border-brand-700 dark:bg-brand-900/30"
                      : "border-slate-200 hover:border-slate-300 dark:border-slate-800 dark:hover:border-slate-700"
                  }`}
                  onClick={() => setSelectedJobId(job.id)}
                >
                  <span className="font-medium">{job.filename || "—"}</span>
                  <span className="text-slate-500 dark:text-slate-400">
                    {formatDateTime(job.created_at)} · Job {job.id.slice(0, 8)}…
                  </span>
                  <span className="text-slate-500 dark:text-slate-400">
                    Status: {job.status || "—"} · Fehler: {job.errors_count ?? 0}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-6">
          <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
            Fehler im ausgewählten Lauf
          </h3>
          <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
            {errors.map((e, i) => (
              <li key={e.id ?? `${i}`}>
                {e.source_table ? `${e.source_table}: ` : ""}
                {e.source_key ? `${e.source_key} – ` : ""}
                {e.reason || "—"}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}

function ProgressBar({ value }: { value: number }) {
  const pct = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0));
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
      <div
        className="h-full rounded-full bg-brand-500 transition-all"
        style={{ width: `${pct}%` }}
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        role="progressbar"
      />
    </div>
  );
}

function Counter({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <span className="text-slate-400">{label}: </span>
      <strong className="text-slate-800 dark:text-slate-100">{value}</strong>
    </div>
  );
}
